import React, { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Clock,
  Eye,
  Loader2,
  Mail,
  PlayCircle,
  RefreshCw,
  Send,
  ShieldOff,
  Timer,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { timeAgo } from "@/lib/timeAgo";

const EVENT_LABELS = {
  invitation_sent: { label: "Email sent", icon: Mail, cls: "text-indigo-600" },
  invitation_resent: { label: "Email resent", icon: Send, cls: "text-indigo-600" },
  invitation_opened: { label: "Magic link opened", icon: Mail, cls: "text-blue-600" },
  confidentiality_acknowledged: { label: "Confidentiality acknowledged", icon: CheckCircle2, cls: "text-emerald-600" },
  token_rotated: { label: "Session token rotated", icon: RefreshCw, cls: "text-slate-500" },
  video_started: { label: "Playback started", icon: PlayCircle, cls: "text-indigo-600" },
  video_paused: { label: "Paused", icon: Clock, cls: "text-slate-500" },
  video_resumed: { label: "Resumed", icon: PlayCircle, cls: "text-indigo-600" },
  video_heartbeat: { label: "Heartbeat", icon: Activity_Dot, cls: "text-slate-400" },
  video_completed: { label: "Completed", icon: CheckCircle2, cls: "text-emerald-600" },
  video_loaded: { label: "Video loaded", icon: PlayCircle, cls: "text-blue-600" },
  expired_attempt: { label: "Expired-link attempt", icon: AlertTriangle, cls: "text-amber-600" },
  revoked_attempt: { label: "Revoked-link attempt", icon: ShieldOff, cls: "text-rose-600" },
  access_revoked: { label: "Access revoked", icon: ShieldOff, cls: "text-rose-600" },
  sendgrid_send_failed: { label: "Email send failed", icon: AlertTriangle, cls: "text-rose-600" },
};

function Activity_Dot(props) {
  return (
    <svg width={props.size || 12} height={props.size || 12} viewBox="0 0 8 8" fill="currentColor" aria-hidden="true">
      <circle cx="4" cy="4" r="2.5" />
    </svg>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatSeconds(s) {
  if (s == null) return "—";
  const total = Math.round(s);
  const mm = Math.floor(total / 60).toString().padStart(2, "0");
  const ss = (total % 60).toString().padStart(2, "0");
  return `${mm}:${ss}`;
}

export default function ShareDetailPage() {
  const { invitation_id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ["share-detail", invitation_id],
    queryFn: async () =>
      (await api.get(`/shares/${invitation_id}`, { params: { audit_limit: 500 } })).data,
    refetchInterval: 20_000,
  });

  const revokeMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/invitations/bulk-revoke`, { invitation_ids: [invitation_id] })).data,
    onSuccess: (data) => {
      const item = data?.items?.[0];
      if (item?.status === "revoked") {
        toast.success("Invitation revoked");
      } else if (item?.status === "already_revoked") {
        toast.info("Invitation was already revoked");
      } else {
        toast.error(item?.error || "Could not revoke");
      }
      queryClient.invalidateQueries({ queryKey: ["share-detail", invitation_id] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const resendMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/invitations/bulk-resend`, { invitation_ids: [invitation_id] })).data,
    onSuccess: (data) => {
      const item = data?.items?.[0];
      if (item?.status === "sent") {
        toast.success("Invitation resent");
      } else if (item?.status === "rate_limited") {
        toast.error(item.error || "Resend cap reached for this invitation");
      } else {
        toast.error(item?.error || "Could not resend");
      }
      queryClient.invalidateQueries({ queryKey: ["share-detail", invitation_id] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  if (detailQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-sm text-slate-500" data-testid="share-detail-loading">
        <Loader2 size={15} className="animate-spin" /> Loading invitation…
      </div>
    );
  }

  if (detailQuery.isError) {
    return (
      <div className="space-y-3 py-12 text-sm text-slate-600" data-testid="share-detail-error">
        <p>{formatApiError(detailQuery.error)}</p>
        <Button variant="outline" size="sm" onClick={() => navigate("/shares")}>
          <ArrowLeft size={13} className="mr-1.5" /> Back to all shares
        </Button>
      </div>
    );
  }

  const data = detailQuery.data || {};
  const inv = data.invitation || {};
  const events = data.audit_events || [];
  const analytics = data.analytics || {};
  const delivery = data.delivery || {};
  const isRevoked = inv.status === "revoked";

  return (
    <div className="space-y-6" data-testid="share-detail-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={() => navigate("/shares")}
            className="mb-1 inline-flex items-center text-[11px] font-medium text-indigo-700 hover:underline"
            data-testid="share-detail-back"
          >
            <ArrowLeft size={11} className="mr-1" /> All shares
          </button>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {inv.invited_email || "Invitation"}
          </h1>
          <p className="text-sm text-slate-500">
            Invitation <code className="font-mono text-[11px]">{inv.invitation_id?.slice(0, 12)}…</code>{" "}
            · status <strong className="text-slate-700">{inv.status}</strong>
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => resendMutation.mutate()}
            disabled={isRevoked || resendMutation.isPending}
            data-testid="share-detail-resend"
          >
            {resendMutation.isPending ? (
              <Loader2 size={13} className="mr-1.5 animate-spin" />
            ) : (
              <Send size={13} className="mr-1.5" />
            )}
            Resend
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
            onClick={() => revokeMutation.mutate()}
            disabled={isRevoked || revokeMutation.isPending}
            data-testid="share-detail-revoke"
          >
            {revokeMutation.isPending ? (
              <Loader2 size={13} className="mr-1.5 animate-spin" />
            ) : (
              <ShieldOff size={13} className="mr-1.5" />
            )}
            Revoke
          </Button>
        </div>
      </header>

      {/* Top stat grid */}
      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="share-detail-stats">
        <Stat label="Run" value={
          <Link to={`/runs/${inv.run_id}`} className="text-indigo-700 hover:underline">
            {inv.run_number != null ? `#${inv.run_number}` : inv.run_id?.slice(0, 8)}
          </Link>
        } sub={inv.workflow_name || inv.run_label || ""} />
        <Stat
          label="Views"
          value={inv.view_count ?? 0}
          sub={`max position ${formatSeconds(analytics.max_playback_position_s)}`}
          icon={<Eye size={14} />}
        />
        <Stat
          label="Watch time"
          value={formatSeconds(analytics.estimated_watch_time_s)}
          sub={`${analytics.distinct_viewing_sessions || 0} session${
            (analytics.distinct_viewing_sessions || 0) === 1 ? "" : "s"
          }`}
          icon={<Timer size={14} />}
        />
        <Stat
          label="Expires"
          value={fmtDate(inv.expires_at)}
          sub={isRevoked ? `Revoked ${fmtDate(inv.revoked_at)}` : `Issued ${fmtDate(inv.issued_at)}`}
          icon={<Clock size={14} />}
        />
      </section>

      {/* Delivery */}
      <section
        className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
        data-testid="share-detail-delivery"
      >
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Mail size={14} className="text-indigo-600" /> Delivery
        </h2>
        <dl className="grid grid-cols-1 gap-3 text-[12px] sm:grid-cols-3">
          <DLItem label="Send count">{delivery.send_count ?? 0}</DLItem>
          <DLItem label="Last send at">{fmtDate(delivery.last_send_at)}</DLItem>
          <DLItem label="Current SendGrid message ID">
            <code className="break-all font-mono text-[10px]">
              {delivery.current_sendgrid_message_id || "—"}
            </code>
          </DLItem>
        </dl>
        {Array.isArray(delivery.send_failures) && delivery.send_failures.length > 0 && (
          <div
            className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2 text-[11px] text-rose-700"
            data-testid="share-detail-send-failures"
          >
            <AlertTriangle size={12} className="mr-1 inline" />
            {delivery.send_failures.length} send failure{delivery.send_failures.length === 1 ? "" : "s"} recorded. Check the timeline below.
          </div>
        )}
      </section>

      {/* Heartbeat density */}
      {analytics.heartbeat_count > 0 && (
        <section
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
          data-testid="share-detail-heartbeat-density"
        >
          <h2 className="mb-3 text-sm font-semibold text-slate-800">
            Heartbeat density · {analytics.heartbeat_count} events
          </h2>
          <HeartbeatBars buckets={analytics.heartbeat_buckets || []} />
        </section>
      )}

      {/* Timeline */}
      <section
        className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
        data-testid="share-detail-timeline"
      >
        <h2 className="mb-3 text-sm font-semibold text-slate-800">
          Timeline · {data.audit_total ?? events.length} events
        </h2>
        {events.length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-500" data-testid="share-detail-timeline-empty">
            No audit events recorded for this invitation yet.
          </div>
        ) : (
          <ol className="space-y-1">
            {events.map((e) => {
              const meta = EVENT_LABELS[e.event_type] || {
                label: e.event_type,
                icon: Activity_Dot,
                cls: "text-slate-500",
              };
              const Icon = meta.icon;
              return (
                <li
                  key={e.event_id}
                  className="flex items-start gap-3 rounded-md px-2 py-1.5 text-xs hover:bg-slate-50"
                  data-testid={`share-detail-event-${e.event_id}`}
                >
                  <span className={`mt-0.5 ${meta.cls}`}>
                    <Icon size={13} />
                  </span>
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-medium text-slate-800">{meta.label}</span>
                      <span
                        className="cursor-help text-[10px] text-slate-500"
                        title={fmtDate(e.at)}
                        data-testid={`share-detail-event-time-${e.event_id}`}
                      >
                        {timeAgo(e.at)}
                      </span>
                    </div>
                    <div className="mt-0.5 text-[10px] text-slate-500">
                      {e.session_id ? (
                        <>session <code className="font-mono">{e.session_id.slice(0, 8)}…</code> · </>
                      ) : null}
                      {e.ip ? <>ip {e.ip} · </> : null}
                      {e.playback_position_s != null ? <>pos {formatSeconds(e.playback_position_s)}</> : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, sub, icon }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between text-[10px] font-medium uppercase tracking-wider text-slate-500">
        <span>{label}</span>
        {icon ? <span className="text-slate-400">{icon}</span> : null}
      </div>
      <div className="mt-1 text-lg font-semibold text-slate-900">{value}</div>
      {sub ? <div className="mt-0.5 text-[10px] text-slate-500">{sub}</div> : null}
    </div>
  );
}

function DLItem({ label, children }) {
  return (
    <div>
      <dt className="text-[10px] font-medium uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-slate-800">{children}</dd>
    </div>
  );
}

function HeartbeatBars({ buckets }) {
  const max = useMemo(() => buckets.reduce((m, b) => Math.max(m, b.count || 0), 0) || 1, [buckets]);
  if (buckets.length === 0) return null;
  return (
    <div className="flex h-16 items-end gap-[2px]" role="img" aria-label="Heartbeat density by 30s window">
      {buckets.map((b) => {
        const pct = Math.round(((b.count || 0) / max) * 100);
        return (
          <div
            key={b.bucket_index}
            className="flex-1 rounded-sm bg-indigo-200"
            style={{ height: `${Math.max(4, pct)}%` }}
            title={`+${Math.round((b.offset_s || 0) / 60)}m · ${b.count} hb`}
          />
        );
      })}
    </div>
  );
}
